import { useState } from "react";
import { CaretDown, Clock, Globe, MapPin, Phone, Storefront } from "@phosphor-icons/react";

// Special-order colors are not on our floor: point the shopper at the supplier's showroom.
// Collapsed by default so the product page stays compact; one tap opens address, map and actions.
export const SeeInPerson = ({ info, color, onArrange, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const rows = [
    info.address && [MapPin, info.address, info.maps_url, "public-see-address"],
    info.hours && [Clock, info.hours, null, "public-see-hours"],
    info.phone && [Phone, info.phone, `tel:${info.phone.replace(/[^\d+]/g, "")}`, "public-see-phone"],
    info.website && [
      Globe,
      info.website.replace(/^https?:\/\//, "").replace(/\/$/, ""),
      info.website,
      "public-see-website",
    ],
  ].filter(Boolean);
  return (
    <section
      className="mt-8 bg-[color:var(--s-cream)]"
      data-testid="public-see-in-person"
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="public-see-body"
        data-testid="public-see-toggle"
        className="flex w-full items-start gap-3 p-5 text-left sm:p-6"
      >
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--s-gold)] text-white">
          <Storefront size={18} weight="bold" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-[color:var(--s-gold)]">
            Slab location
          </span>
          <h3 className="mt-0.5 text-lg font-semibold tracking-tight" data-testid="public-see-title">
            See it in person
          </h3>
          <span className="mt-1.5 block text-sm leading-relaxed text-[color:var(--s-mute)]">
            {info.kind === "ours" ? (
              <>
                {color} is on our floor — come see the actual slabs at{" "}
                <strong className="text-[color:var(--s-ink)]" data-testid="public-see-supplier">
                  {info.supplier}
                </strong>
                {open ? " and pick the one you like." : "."}
              </>
            ) : (
              <>
                {color} is a special-order color — we bring it in for your job. View full slabs at{" "}
                <strong className="text-[color:var(--s-ink)]" data-testid="public-see-supplier">
                  {info.supplier}
                </strong>
                {open ? " and tell us which slab you like." : "."}
              </>
            )}
          </span>
          {!open && (
            <span
              className="mt-2 inline-block text-xs font-semibold text-[color:var(--s-gold)]"
              data-testid="public-see-toggle-hint"
            >
              Show address, hours{info.map_embed_url ? " & map" : ""}
            </span>
          )}
        </span>
        <CaretDown
          size={18}
          weight="bold"
          className={`mt-2 shrink-0 text-[color:var(--s-gold)] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <div id="public-see-body" className="px-5 pb-5 sm:px-6 sm:pb-6" data-testid="public-see-body">
          {rows.length > 0 && (
            <ul className="grid gap-2 sm:grid-cols-2" data-testid="public-see-details">
              {rows.map(([Icon, text, href, tid]) => (
                <li key={tid} className="flex items-start gap-2.5 text-sm">
                  <Icon size={16} weight="bold" className="mt-0.5 shrink-0 text-[color:var(--s-gold)]" />
                  {href ? (
                    <a
                      href={href}
                      target={href.startsWith("http") ? "_blank" : undefined}
                      rel="noreferrer"
                      data-testid={tid}
                      className="break-words underline-offset-4 hover:underline"
                    >
                      {text}
                    </a>
                  ) : (
                    <span data-testid={tid} className="break-words">
                      {text}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {info.map_embed_url && (
            <div
              className="mt-4 overflow-hidden  border border-[color:var(--s-gold)]/30 bg-[#eeece7]"
              data-testid="public-see-map"
            >
              <iframe
                title={`Map to ${info.supplier}`}
                src={info.map_embed_url}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="block h-44 w-full border-0 sm:h-52"
                data-testid="public-see-map-frame"
              />
              <a
                href={info.maps_url}
                target="_blank"
                rel="noreferrer"
                data-testid="public-see-map-open"
                className="flex items-center justify-between gap-3 px-3 py-2 text-xs text-[color:var(--s-mute)] hover:text-[color:var(--s-ink)]"
              >
                <span className="truncate">{info.address}</span>
                <span className="shrink-0 font-semibold text-[color:var(--s-gold)]">Open in Maps →</span>
              </a>
            </div>
          )}
          {info.notes && (
            <p
              className="mt-3 whitespace-pre-wrap text-xs leading-relaxed text-[color:var(--s-mute)]"
              data-testid="public-see-notes"
            >
              {info.notes}
            </p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {info.maps_url && (
              <a
                href={info.maps_url}
                target="_blank"
                rel="noreferrer"
                data-testid="public-see-directions"
                className="s-act s-act-gold !w-auto"
              >
                <MapPin size={16} weight="bold" /> Get directions
              </a>
            )}
            {info.phone && (
              <a
                href={`tel:${info.phone.replace(/[^\d+]/g, "")}`}
                data-testid="public-see-call"
                className="s-act !w-auto"
              >
                <Phone size={16} weight="bold" /> Call {info.supplier}
              </a>
            )}
            <button
              type="button"
              onClick={onArrange}
              data-testid="public-see-arrange"
              className="s-act !w-auto"
            >
              <Storefront size={16} weight="bold" /> Arrange a viewing
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default SeeInPerson;
